import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState, useMemo } from 'react';
import { authService } from '../../services/auth/authService';
import { AUTH_CONFIG, DEV_USER } from '../../config/config';

/**
 * Hook personalizado para manejar autenticación.
 * 
 * Proporciona una interfaz uniforme independientemente del mecanismo
 * de autenticación (dev o auth0).
 * 
 * El consumidor de este hook no necesita conocer la implementación.
 */
export function useAuth() {
  // Siempre llamar todos los hooks primero
  const auth0 = useAuth0();
  const [isReady, setIsReady] = useState(false);
  
  const { isAuthenticated, isLoading, user, getAccessTokenSilently, loginWithRedirect } = auth0;
  
  // Determinar modo usando useMemo (también es un hook, debe llamarse siempre)
  const isDevMode = useMemo(() => authService.isDevMode(), []);
  
  // Registrar función de login de Auth0 en authService
  useEffect(() => {
    if (!isDevMode && loginWithRedirect) {
      authService.setAuth0Login(() => loginWithRedirect());
    }
  }, [isDevMode, loginWithRedirect]);
  
  // Efecto para sincronizar token (siempre se llama, pero solo actúa en modo auth0)
  useEffect(() => {
    if (isDevMode) {
      setIsReady(true);
      return;
    }
    
    if (isLoading) {
      return;
    }
    
    const syncToken = async () => {
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          sessionStorage.setItem('token', token);
          if (user) {
            sessionStorage.setItem('user', JSON.stringify(user));
          }
        } catch (error) {
          console.error('[useAuth] Error getting token:', error);
        }
      }
      setIsReady(true);
    };
    
    syncToken();
  }, [isDevMode, isAuthenticated, isLoading, getAccessTokenSilently, user]);
  
  // Logout que limpia sessionStorage
  const logout = useMemo(() => {
    return () => {
      authService.clearSession();
      auth0.logout({ logoutParams: { returnTo: window.location.origin } });
    };
  }, [auth0]);
  
  // En modo dev, verificar si hay sesión DEV activa
  if (isDevMode) {
    const devAuthenticated = sessionStorage.getItem('dev_authenticated') === 'true';
    const userStr = sessionStorage.getItem('user');
    let devUser = null;
    
    if (devAuthenticated && userStr) {
      try {
        devUser = JSON.parse(userStr);
      } catch (e) {
        console.error('[useAuth] Error parsing user, using DEV_USER fallback');
        devUser = DEV_USER;
      }
    }
    
    return {
      isAuthenticated: devAuthenticated,
      isLoading: false,
      isReady: true,
      user: devUser,
      logout: () => {
        authService.clearSession();
        window.location.reload();
      },
      getAccessTokenSilently: () => Promise.resolve(authService.getToken()),
      loginWithRedirect: () => authService.login(),
    };
  }
  
  return {
    isAuthenticated,
    isLoading,
    isReady,
    user,
    logout,
    getAccessTokenSilently,
    loginWithRedirect,
  };
}

// Re-exportar para conveniencia
export { AUTH_CONFIG };

